import BlockIcon from "@mui/icons-material/Block";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { IPromotion } from "../../../../../../types";
import Asterisk from "../../../Asterisk/Asterisk";
import { headingSx } from "../../styles";

export interface PromotionSelectProps {
  promotions: IPromotion[];
  selectedValue?: IPromotion;
  onSelect?: (promotion: IPromotion) => void;
}

const PromotionMenuItemValue = ({
  id,
  description,
}: Pick<IPromotion, "description" | "id">) => {
  const theme = useTheme();

  return (
    <Box>
      <Typography>{id}</Typography>
      <Typography sx={{ color: theme.tokens.FuelColorTextTertiary }}>
        {description}
      </Typography>
    </Box>
  );
};

const PromotionSelect = ({
  promotions = [],
  selectedValue,
  onSelect = () => {},
}: PromotionSelectProps) => {
  const theme = useTheme();
  const { t } = useTranslation("orders");

  const isEmpty = promotions.length === 0;

  const renderValue = (inputValue: string) => {
    if (!inputValue) {
      return t("orderEntry.summary.header.promotionId.select.placeholder");
    }
    const promotion = promotions.find(({ id }) => id === inputValue);
    return `${promotion?.id} (${promotion?.description})`;
  };

  const isValidValue = promotions.some(({ id }) => id === selectedValue?.id);

  return (
    <FormControl fullWidth>
      <Box sx={headingSx(theme)}>
        {t("orderEntry.summary.header.promotionId")}
        <Asterisk />
      </Box>

      <Select
        data-testid="promotion-select"
        id="promotion-select"
        name="promotion-select"
        value={!isValidValue ? "" : selectedValue?.id}
        IconComponent={KeyboardArrowDownIcon}
        renderValue={renderValue}
        displayEmpty
        autoWidth={isEmpty}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          PaperProps: {
            sx: {
              maxHeight: "400px",
            },
          },
          MenuListProps: {
            sx: {
              maxWidth: "unset !important",
              "& .MuiMenuItem-root": {
                "&.Mui-focusVisible": {
                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                  boxShadow: "none",
                },
              },
            },
          },
        }}
      >
        {isEmpty ? (
          <Box
            sx={{
              padding: theme.tokens.FuelSpacing4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              width: "488px",
              height: "150px",
              color: theme.tokens.FuelColorTextTertiary,
            }}
          >
            <BlockIcon />

            <Typography
              sx={{
                textAlign: "center",
                fontWeight: theme.tokens.FuelFontWeightRegular,
                fontSize: theme.tokens.FuelFontSizeMd,
                color: theme.tokens.FuelColorTextPrimary,
              }}
              data-testid="no-promotions-available"
            >
              {t("orderEntry.noPromotionFound.title")}
            </Typography>
          </Box>
        ) : (
          promotions.map((promotion) => (
            <MenuItem
              data-testid={`option-promotion-${promotion.id}`}
              key={promotion.id}
              value={promotion.id}
              onClick={() => onSelect(promotion)}
            >
              <PromotionMenuItemValue
                id={promotion.id}
                description={promotion.description}
              />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export default PromotionSelect;
